// Readiness math (docs/qlearn-performance-readiness-design.md §3b/§3c,
// slice 2). Pure functions, explicit `now`, question-weighted throughout.
//
// Every default below marked [Qn-default] was packeted to Akshay by F8-orch
// (2026-07-06) and is a single constant to change — reversible by design.
// The verdict NEVER renders below the evidence floor: the card shows what
// basis it has and says "not enough recent data" instead (abstain).

import { WINDOW_DAYS, windowStats, trendDelta } from "./performance-trend";

export const READINESS_DEFAULTS = {
  // [Q1-default] house score-band `strong` threshold; the ABSITE-calibrated
  // target is an open content decision.
  targetPct: 75,
  borderlineMargin: 5,
  // [Q4-default] evidence floor: below EITHER bound the verdict abstains.
  minQuestions: 100,
  minQuizzes: 3,
  windowDays: WINDOW_DAYS,
  // §3c: never name a "weak area" on fewer than 20 recent questions.
  weakAreaMinQuestions: 20,
  weakAreaCount: 2,
  // Coverage floor for the top verdict (share of the student's own lifetime
  // disciplines practiced recently). Default-used, not Akshay-blessed yet.
  coverageFloorRatio: 0.5
};

export const VERDICTS = {
  AHEAD: "ahead",
  NEAR: "near",
  BELOW: "below",
  ABSTAIN: "abstain"
};

// [Q2-default] softer copy variant chosen over "On track / Needs work".
export const VERDICT_COPY = {
  [VERDICTS.AHEAD]: "Ahead of your target",
  [VERDICTS.NEAR]: "Near your target",
  [VERDICTS.BELOW]: "Below your target",
  [VERDICTS.ABSTAIN]: "Not enough recent data for a readiness read"
};

// [Q3-default] unaided accuracy is the readiness question.
export const excludeTutorAssisted = (points) =>
  points.filter((point) => !point.tutorAssisted);

const inWindow = (points, now, days) => {
  const start = now - days * 86400000;
  return points.filter((point) => point.time > start && point.time <= now);
};

// Per-discipline windowed accuracy. A quiz spanning several disciplines
// contributes its full weight to each — quiz-level rows are the granularity
// we honestly have (design §2); stated rather than hidden.
export const disciplineStats = (points, now, days = WINDOW_DAYS) => {
  const stats = new Map();

  inWindow(points, now, days).forEach((point) => {
    point.disciplines.forEach((discipline) => {
      const entry = stats.get(discipline) || { discipline, questions: 0, correct: 0 };
      entry.questions += point.total;
      entry.correct += point.correct;
      stats.set(discipline, entry);
    });
  });

  return [...stats.values()].map((entry) => ({
    ...entry,
    accuracy: Math.round((entry.correct / entry.questions) * 100)
  }));
};

// Coverage: how much of the student's OWN lifetime discipline spread was
// practiced recently. Their history is the denominator — no invented
// curriculum list.
export const coverage = (points, now, days = WINDOW_DAYS) => {
  const lifetime = new Set();
  points.forEach((point) => point.disciplines.forEach((d) => lifetime.add(d)));
  const recent = new Set();
  inWindow(points, now, days).forEach((point) => point.disciplines.forEach((d) => recent.add(d)));

  return { recent: recent.size, lifetime: lifetime.size };
};

// Weakest recently-practiced disciplines (lowest accuracy, evidence-floored
// per §3c), plus lifetime disciplines NOT seen in the window (stalest first)
// so "thin spots" covers both failure modes: practiced-and-weak, and
// not-practiced-lately.
export const weakAreas = (points, now, opts = READINESS_DEFAULTS) => {
  const recentStats = disciplineStats(points, now, opts.windowDays);
  const weak = recentStats
    .filter((entry) => entry.questions >= opts.weakAreaMinQuestions)
    .sort((a, b) => a.accuracy - b.accuracy)
    .slice(0, opts.weakAreaCount);

  const recentNames = new Set(recentStats.map((entry) => entry.discipline));
  const lastSeen = new Map();
  points.forEach((point) =>
    point.disciplines.forEach((d) => lastSeen.set(d, Math.max(lastSeen.get(d) || 0, point.time)))
  );
  const stale = [...lastSeen.entries()]
    .filter(([discipline]) => !recentNames.has(discipline))
    .sort((a, b) => a[1] - b[1])
    .slice(0, opts.weakAreaCount)
    .map(([discipline, time]) => ({ discipline, lastSeen: time }));

  return { weak, stale };
};

export const readinessBasis = (allPoints, now, opts = READINESS_DEFAULTS) => {
  const points = excludeTutorAssisted(allPoints);
  const current = windowStats(points, now, opts.windowDays);
  const { delta, deltaExact, prior } = trendDelta(points, now, opts.windowDays);

  return {
    current,
    delta,
    deltaExact,
    priorHasData: prior.accuracy !== null,
    coverage: coverage(points, now, opts.windowDays),
    ...weakAreas(points, now, opts),
    // Rendered inside the "last N days" basis line, so it must be scoped to
    // that window — a tutor quiz from months ago is not part of this basis
    // and must not inflate the count (#522 primary-gate blocker).
    excludedQuizzes: inWindow(allPoints, now, opts.windowDays).filter(
      (point) => point.tutorAssisted
    ).length
  };
};

export const readinessVerdict = (basis, opts = READINESS_DEFAULTS) => {
  const { current, deltaExact } = basis;

  if (current.questions < opts.minQuestions || current.quizzes < opts.minQuizzes) {
    return VERDICTS.ABSTAIN;
  }

  // F1 (#522 gate): the verdict gates on the UNROUNDED ratio and delta —
  // display rounding (74.5 → "75%", −0.9 → "0 pts") must never upgrade
  // below-target-and-falling into the top verdict. Rounded values are for
  // the card's text only.
  const accuracyExact = (current.correct / current.questions) * 100;
  const trendNotFalling = deltaExact === null || deltaExact >= 0;
  const coverageOk =
    basis.coverage.lifetime === 0 ||
    basis.coverage.recent / basis.coverage.lifetime >= opts.coverageFloorRatio;

  if (accuracyExact >= opts.targetPct && trendNotFalling && coverageOk) {
    return VERDICTS.AHEAD;
  }
  // Within the margin below target, or at/above target but with a falling
  // trend / thin coverage: close, not there.
  if (accuracyExact >= opts.targetPct - opts.borderlineMargin) {
    return VERDICTS.NEAR;
  }

  return VERDICTS.BELOW;
};
